import useIsMobile from '@client/core/CustomHooks/useIsMobile';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Carousel from './Carousel';
import DraggableCarousel from './DraggableCarousel';
import gsap from 'gsap';
import LeftArrowButton from '@client/assets/svgs/left-arrow-button.svg';
import RightArrowButton from '@client/assets/svgs/right-arrow-button.svg';
import Functions from '@client/style/Functions';
import { Breakpoints } from '@client/style/Variables';

const StyledCarousel = styled(Carousel)`
	padding: 0 var(--gridMargin);
	height: fit-content;
`;

const StyledDraggableCarousel = styled(DraggableCarousel)`
	padding: 0 var(--gridMargin);
`;

const PointerCatcher = styled.div`
	position: absolute;
	width: 100vw;
	padding: 0 var(--gridMargin);
	height: 100%;
	left: 0;
	top: 0;

	display: none;
	justify-content: space-between;
	align-items: center;

	pointer-events: none;

	svg {
		pointer-events: all;
		cursor: pointer;
	}

	${Functions.breakpoint(Breakpoints.mobile)} {
		display: flex;
	}
`;

export default function MagicCarouselSelector(props: { onChange: (index: number) => void; children: ReactNode }) {
	if (!props.children) return null;

	const isMobile = useIsMobile();
	const [activeIndex, setActiveIndex] = useState(0);
	const leftTriggerRef = useRef<SVGSVGElement | null>(null);
	const rightTriggerRef = useRef<SVGSVGElement | null>(null);

	useEffect(() => {
		shouldShowBackButton();
		shouldShowNextButton();
		props.onChange(activeIndex);
	}, [activeIndex]);

	useEffect(() => {
		shouldShowBackButton();
		shouldShowNextButton();
	}, [props.children]);

	const incrementActiveIndex = () => {
		if (activeIndex < React.Children.count(props.children) - 1) {
			setActiveIndex(activeIndex + 1);
		} else {
			setActiveIndex(0);
		}
	};

	const decrementActiveIndex = () => {
		if (activeIndex > 0) {
			setActiveIndex(activeIndex - 1);
		} else {
			setActiveIndex(React.Children.count(props.children) - 1);
		}
	};

	const shouldShowBackButton = () => {
		if (!leftTriggerRef.current) return;
		if (activeIndex === 0) gsap.to(leftTriggerRef.current, { opacity: 0, duration: 0.25, pointerEvents: 'none' });
		else gsap.to(leftTriggerRef.current, { opacity: 1, duration: 0.25, pointerEvents: 'all' });
	};

	const shouldShowNextButton = () => {
		if (!rightTriggerRef.current) return;
		if (activeIndex === React.Children.count(props.children) - 1)
			gsap.to(rightTriggerRef.current, { opacity: 0, duration: 0.25, pointerEvents: 'none' });
		else gsap.to(rightTriggerRef.current, { opacity: 1, duration: 0.25, pointerEvents: 'all' });
	};

	return (
		<>
			{isMobile ? (
				<StyledDraggableCarousel activeIndex={activeIndex} dragToNewIndex={setActiveIndex}>
					{props.children}
				</StyledDraggableCarousel>
			) : (
				<>
					<StyledCarousel activeIndex={activeIndex}>{props.children}</StyledCarousel>
					<PointerCatcher>
						<LeftArrowButton onClick={decrementActiveIndex} ref={leftTriggerRef} />
						<RightArrowButton onClick={incrementActiveIndex} ref={rightTriggerRef} />
					</PointerCatcher>
				</>
			)}
		</>
	);
}
